import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCreateFolder, useModifyDocumentAttributes } from './hooks';
import {
  PendingCategoryError,
  type AddCategoryAndFileInput,
  type AddCategoryAndFileResult,
  type CategoryImportResponse,
} from '@dataroom/shared';

export { PendingCategoryError };
export type { AddCategoryAndFileInput, AddCategoryAndFileResult };

/**
 * "Add a new category…" from the review panel, as ONE action: create the
 * category inside its parent through the existing folder import (the
 * New-folder path — `parentTagId`, so it shares the parent's tier, ADR-0010 §2;
 * the import itself reuses a same-named sibling) and then file the document
 * there through the category PATCH, which records the person's decision for the
 * learning loop (ADR-0020) exactly as a review round or a branch change does.
 * Both endpoints are the room admin's (the seller portal has its own copy of this
 * hook, for the seller side's one-subfolder import), so this needs no permission
 * of its own.
 */
export function useAddCategoryAndFile(roomId: string) {
  const createFolder = useCreateFolder(roomId);
  const modify = useModifyDocumentAttributes(roomId);
  return useMutation({
    mutationFn: async (input: AddCategoryAndFileInput): Promise<AddCategoryAndFileResult> => {
      let categoryId = input.existingId;
      let created = false;
      if (categoryId === null) {
        const res = (await createFolder.mutateAsync({
          nodes: [{ name: input.name }],
          parentTagId: input.parentId,
        })) as CategoryImportResponse;
        const node = res.nodes[0];
        if (!node) throw new Error('The category could not be created.');
        if (!node.created && !input.knownIds.has(node.id)) throw new PendingCategoryError();
        categoryId = node.id;
        created = node.created;
      }
      await modify.mutateAsync({
        documentId: input.documentId,
        body: { categoryTagIds: input.categoryIdsFor(categoryId) },
      });
      return { categoryId, created };
    },
  });
}

export interface CreateCategoryHereInput {
  /** The level's parent; null = a new top-level category. */
  parentId: string | null;
  /** Already cleaned (cleanCategoryName) and validated. */
  name: string;
  /** Every approved category id the room lists (to spot a reused pending one). */
  knownIds: ReadonlySet<string>;
}

/**
 * "+ New category here" on a filing level (owner ask 2026-10-15): create ONE
 * category under the level's parent through the same folder import as "Add a
 * new category…" (inside a parent it shares the parent's tier; at the top level,
 * with no tier given, it is due-diligence material — the most restrictive), and
 * wait for the room's category list to include it. Filing the document there is
 * the caller's — exactly as choosing an existing option. No new endpoint.
 */
export function useCreateCategoryHere(roomId: string) {
  const qc = useQueryClient();
  const createFolder = useCreateFolder(roomId);
  return useMutation({
    mutationFn: async (
      input: CreateCategoryHereInput,
    ): Promise<{ id: string; name: string; created: boolean }> => {
      const res = (await createFolder.mutateAsync({
        nodes: [{ name: input.name }],
        ...(input.parentId ? { parentTagId: input.parentId } : {}),
      })) as CategoryImportResponse;
      const node = res.nodes[0];
      if (!node) throw new Error('The category could not be created.');
      if (!node.created && !input.knownIds.has(node.id)) throw new PendingCategoryError();
      // The next level is worked out from the tree: have the new category in it
      // before the document is filed there.
      await qc.refetchQueries({ queryKey: ['rooms', roomId, 'categories'], exact: true });
      return { id: node.id, name: node.name, created: node.created };
    },
  });
}
